import { createStore } from "zustand-x";
import { createJSONStorage } from "zustand/middleware";
import { actions } from "../useStore";
import { IAuthenticationInterface } from "../../models/interfaces/core/Authentication.interface";

const initialState: IAuthenticationInterface = {
  user: null,
  isAuthenticated: false,
  permissions: [],
  activeRole: {},
  activeDeviceIdentifier: "",
  deviceIdentifiers: [],
  authToken: "",
  authTokenExpiresAt: 0,
  refreshToken: "",
  refreshTokenExpiresAt: 0,
  isRefreshing: false,
};

const authStore = createStore<IAuthenticationInterface>(initialState, {
  name: "ymXo8h",
  immer: {
    enabled: true,
  },
  persist: {
    enabled: true,
    storage: createJSONStorage(() => localStorage),
  },
})
  .extendSelectors((state, get, api) => ({
    getDeviceIdentifier: (
      deviceIdentifier: string,
      deviceIdentifierType: "hashed_email" | "hashed_phone" | "identifier"
    ) => {
      const device = get.deviceIdentifiers().find((device: any) => {
        return device?.[deviceIdentifierType] === deviceIdentifier;
      });

      if (device) {
        return device.device_identifier;
      }

      return "";
    },
    roles: () => {
      return get.user()?.identityInfo ?? [];
    },
  }))
  .extendActions((state, get, api) => ({
    login: (
      user: any,
      step: number,
      permissions?: any,
      activeRole?: any,
      tokens?: { accessToken: string; refreshToken: string }
    ) => {
      state.user(user);

      if (step === 2) {
        state.isAuthenticated(true);
        state.permissions(permissions);
        state.activeRole(activeRole);

        //Decode JWT token to get token expiration time
        if (tokens) {
          // --- Access Token --- //
          const accessTokenExpiresAt =
            (JSON.parse(atob(tokens?.accessToken?.split(".")[1])) as any).exp *
            1000;

          state.authToken(tokens?.accessToken);
          state.authTokenExpiresAt(accessTokenExpiresAt);

          // --- Refresh Token --- //
          const refreshTokenExpiresAt =
            (JSON.parse(atob(tokens?.refreshToken?.split(".")[1])) as any).exp *
            1000;

          state.refreshToken(tokens?.refreshToken);
          state.refreshTokenExpiresAt(refreshTokenExpiresAt);
        }
      }
    },
    logout: () => {
      const _state = {
        ...initialState,
        deviceIdentifiers: get.deviceIdentifiers(),
      };
      state.state(_state, true);
      actions.core.route("/signin");
    },
    updateSession: (
      authToken?: string,
      authTokenExpiresAt?: number,
      newRefreshToken?: string,
      refreshTokenExpiresAt?: number,
      user?: any
    ) => {
      authToken && state.authToken(authToken);
      authTokenExpiresAt && state.authTokenExpiresAt(authTokenExpiresAt);
      newRefreshToken && state.refreshToken(newRefreshToken);
      refreshTokenExpiresAt &&
        state.refreshTokenExpiresAt(refreshTokenExpiresAt);

      user && state.user(user);
    },
    updatePermissions: (permissions: any) => {
      state.permissions(permissions);
    },
    setActiveRole: (activeRole: any) => {
      state.activeRole(activeRole);
    },

    /* ---------------------------------------------------------------------------------------------- */
    /*                                        Device Identifier                                       */
    /* ---------------------------------------------------------------------------------------------- */

    isDeviceIdentifierRegistered: (uniqueDeviceIdentifier: string) => {
      const result = get.deviceIdentifiers().findIndex((device: any) => {
        return (
          device["identifier"] === uniqueDeviceIdentifier ||
          device["hashed_email"] === uniqueDeviceIdentifier ||
          device["hashed_phone"] === uniqueDeviceIdentifier
        );
      });

      return result !== -1;
    },

    addDeviceIdentifier: (
      deviceIdentifier: string,
      uniqueIdentifier: string,
      hashedEmail: string,
      hashedPhone: string
    ) => {
      const hasDeviceIdentifierBefore =
        actions.auth.isDeviceIdentifierRegistered(uniqueIdentifier);

      if (hasDeviceIdentifierBefore) {
        const deviceIdentifiers = get.deviceIdentifiers();

        const deviceIndex = deviceIdentifiers.findIndex((device: any) => {
          return device.identifier === uniqueIdentifier;
        });

        const updatedDeviceIdentifiers = deviceIdentifiers.map(
          (device: any, index: number) => {
            if (index === deviceIndex) {
              return {
                ...device,
                device_identifier: deviceIdentifier,
              };
            }
            return device;
          }
        );

        state.deviceIdentifiers(updatedDeviceIdentifiers);
        state.activeDeviceIdentifier(deviceIdentifier);
      } else {
        const currentDeviceIdentifiers = get.deviceIdentifiers();

        // If we have reached max limit, remove the oldest device
        if (currentDeviceIdentifiers.length >= 5) {
          currentDeviceIdentifiers.shift();
        }

        state.deviceIdentifiers([
          ...currentDeviceIdentifiers,
          {
            identifier: uniqueIdentifier,
            device_identifier: deviceIdentifier,
            hashed_email: hashedEmail,
            hashed_phone: hashedPhone,
            has_device_identifier: "yes",
          },
        ]);

        state.activeDeviceIdentifier(deviceIdentifier);
      }
    },

    /* ---------------------------------------------------------------------------------------------- */
  }));

export default authStore;
